/**
 * Magnetic declination at Caslino al Piano (45.7236 N, 9.0397 E), November 2026,
 * from the World Magnetic Model WMM-2025 (computed with pygeomag on 2026-11-03): +3.69° (east).
 * It changes by about +0.1° per year: fine to keep until the next WMM release.
 */
export const DECLINATION_DEG = 3.69;

const norm = (d: number) => ((d % 360) + 360) % 360;

export function trueHeading(magneticDeg: number, declination = DECLINATION_DEG): number {
  return norm(magneticDeg + declination);
}

type OrientationLike = { webkitCompassHeading?: number; alpha: number | null; absolute?: boolean };

/** Heading in degrees from true north, or null when the event has no compass data. */
export function headingFromEvent(e: OrientationLike): number | null {
  if (typeof e.webkitCompassHeading === 'number' && Number.isFinite(e.webkitCompassHeading)) {
    return trueHeading(e.webkitCompassHeading);
  }
  // Chrome's absolute orientation: alpha is counter-clockwise from true north
  if (e.absolute && typeof e.alpha === 'number') return norm(360 - e.alpha);
  return null;
}

/** Mean of angles in degrees, robust across north. */
export function circularMean(degs: number[]): number {
  let x = 0;
  let y = 0;
  for (const d of degs) {
    x += Math.cos((d * Math.PI) / 180);
    y += Math.sin((d * Math.PI) / 180);
  }
  const m = norm((Math.atan2(y, x) * 180) / Math.PI);
  return Math.abs(m - 360) < 1e-9 ? 0 : m;
}

export type CompassProblem = 'permesso-negato' | 'non-supportato';

/**
 * Start listening to the compass. Must be called from a user tap (iOS permission prompt).
 * Returns a stop function. Reports null + reason when the compass cannot be used.
 */
export async function startCompass(onHeading: (deg: number | null, reason?: CompassProblem) => void): Promise<() => void> {
  const DOE = (globalThis as { DeviceOrientationEvent?: { requestPermission?: () => Promise<string> } }).DeviceOrientationEvent;
  if (!DOE) {
    onHeading(null, 'non-supportato');
    return () => {};
  }
  if (typeof DOE.requestPermission === 'function') {
    let res = 'denied';
    try {
      res = await DOE.requestPermission();
    } catch {
      res = 'denied';
    }
    if (res !== 'granted') {
      onHeading(null, 'permesso-negato');
      return () => {};
    }
  }
  const recent: number[] = [];
  let got = false;
  const handler = (ev: Event | OrientationLike) => {
    const h = headingFromEvent(ev as OrientationLike);
    if (h === null) return;
    got = true;
    recent.push(h);
    if (recent.length > 5) recent.shift();
    onHeading(circularMean(recent));
  };
  window.addEventListener('deviceorientationabsolute', handler as EventListener);
  window.addEventListener('deviceorientation', handler as EventListener);
  const timer = window.setTimeout(() => {
    if (!got) onHeading(null, 'non-supportato');
  }, 3000);
  return () => {
    window.clearTimeout(timer);
    window.removeEventListener('deviceorientationabsolute', handler as EventListener);
    window.removeEventListener('deviceorientation', handler as EventListener);
  };
}
